import { Component, StatelessComponent, ComponentClass } from 'react'
import * as diff from 'lodash/difference'
import { WrappedFieldArrayProps } from 'redux-form'
import * as styles from 'styles/molecules/forms/OnboardingForm.css'
import Button from 'components/features/pfm/Button/Button'
import { generate } from 'randomstring'
import { QAMarked } from 'interfaces/component'

export type PropertyListChildProps = {
  name: string
  index: number
}

export type PropertyListOwnProps<T extends {}> = {
  addLabel: string
  child: ComponentClass<PropertyListChildProps> | StatelessComponent<PropertyListChildProps>
  initialValues?: T
  onFieldAdded?: (index: number) => void
  destroyOnUnmount?: boolean
}

export type PropertyListProps<T extends {}> = QAMarked & PropertyListOwnProps<T> & WrappedFieldArrayProps<T>

type PropertyListState<T> = {
  ids: string[]
  fields: PropertyListProps<T>['fields']
}

class PropertyList<T extends {}> extends Component<PropertyListProps<T>, PropertyListState<T>> {
  static defaultProps = {
    initialValues: {},
    destroyOnUnmount: true,
  }

  static getDerivedStateFromProps (nextProps: PropertyListProps<any>, prevState: PropertyListState<any>) {
    const nextFields = nextProps.fields
    const prevFields = prevState.fields
    let ids: string[] = []

    if (nextFields.length > prevFields.length) {
      ids = prevState.ids.concat(generate())
    }

    if (nextFields.length < prevFields.length) {
      const prev = prevFields.getAll()
      const next = nextFields.getAll()
      const removed = diff(prev, next)

      const removedIndexes = removed.map((field) => prev.indexOf(field))
      ids = prevState.ids.filter((_id, idx) => removedIndexes.indexOf(idx) === -1)
    }

    return { ids }
  }

  constructor (props: PropertyListProps<T>) {
    super(props)
    this.state = {
      // tslint:disable-next-line:no-unnecessary-callback-wrapper
      ids: props.fields.map(() => generate()),
      fields: props.fields,
    }
  }

  render () {
    const { fields, addLabel, qaMarker, child: Child } = this.props

    // Child components are stateful and should be coupled with their data.
    // So we should support all keys manually.
    return (
      <div>
        { fields.map((item, index) => (
          <div className={ styles.listArrayItem } key={ this.state.ids[index] }>
            <Child name={ item } index={ index } />
          </div>
        ))}
        <div>
          <Button style='hollow' fullWidth qaMarker={ qaMarker } onClick={ this.addField }>
            { addLabel }
          </Button>
        </div>
      </div>
    )
  }

  componentDidMount () {
    if (this.props.fields.length === 0) {
      this.addField()
    }
  }

  shouldComponentUpdate (nextProps: PropertyListProps<T>, nextState: PropertyListState<T>) {
    return nextState !== this.state ||
      nextProps.fields.length !== this.props.fields.length
  }

  componentWillUnmount () {
    const { fields, destroyOnUnmount } = this.props

    if (destroyOnUnmount) {
      // TODO @rendalf remove when typings get well
      (fields as any).removeAll()
    }
  }

  private addField = () => {
    const { fields, onFieldAdded } = this.props
    const initialValues = this.props.initialValues as T
    const nextLastNumber = fields.length
    fields.push(initialValues)

    if (onFieldAdded) {
      setTimeout(() => {
        onFieldAdded(nextLastNumber)
      }, 25)
    }
  }
}

export default PropertyList
